import { useEffect, useState } from 'react';
import { Button, Image, Spin, Upload } from 'antd';
import {
  CameraOutlined,
  CheckCircleFilled,
  CheckOutlined,
  CloudUploadOutlined,
  ExclamationCircleFilled,
  ReloadOutlined,
  ZoomInOutlined,
} from '@ant-design/icons';
import { useLanguage } from 'hooks/Language/useLanguage';
import { IKycDocumentState } from 'utils/kycStatus';
import { MAX_KYC_FILE_MB, StepStatus } from 'hooks/useKycUpload';
import SelfieCamera from './SelfieCamera';

/** Images only: presence is verified by loading the file as an image. */
export const ACCEPTED_TYPES = 'image/png,image/jpeg';

interface Props {
  doc: IKycDocumentState;
  /** Local preview of a picked, not yet confirmed, file. */
  draftUrl?: string;
  /** Preview of the document already on file. */
  savedUrl?: string;
  status: StepStatus;
  errorMessage?: string;
  onPick: (file: File) => void;
  onConfirm: () => void;
  onDiscard: () => void;
}

const KycDocumentStep: React.FC<Props> = ({
  doc,
  draftUrl,
  savedUrl,
  status,
  errorMessage,
  onPick,
  onConfirm,
  onDiscard,
}) => {
  const { translate } = useLanguage();
  const isUploading = status === 'uploading';
  const isSaved = doc.presence === 'present' && !draftUrl;
  // Selfie only: the saved one is being retaken with the camera.
  const [retaking, setRetaking] = useState(false);
  // Once a selfie was taken here, retakes reopen the camera directly.
  const [cameraUsed, setCameraUsed] = useState(false);
  useEffect(() => {
    setRetaking(false);
    setCameraUsed(false);
  }, [doc.key]);
  const showCamera =
    doc.liveCapture && !draftUrl && (!isSaved || retaking);

  return (
    <div className="kyc-step">
      <header className="kyc-step__head">
        <h3>
          {translate(doc.label)}
          {!doc.required && (
            <span className="kyc-step__optional">
              {translate('Optional')}
            </span>
          )}
        </h3>
        <p>{translate(doc.hint)}</p>
      </header>

      <ul className="kyc-step__tips">
        {doc.tips.map((tip) => (
          <li key={tip}>
            <CheckOutlined />
            {translate(tip)}
          </li>
        ))}
      </ul>

      {showCamera ? (
        <SelfieCamera
          onCapture={(file) => {
            setRetaking(false);
            setCameraUsed(true);
            onPick(file);
          }}
          onCancel={retaking ? () => setRetaking(false) : undefined}
          autoStart={retaking || cameraUsed}
        />
      ) : draftUrl || isSaved ? (
        <figure
          className={`kyc-preview${isSaved ? ' kyc-preview--saved' : ''}`}
        >
          <Image
            src={draftUrl || savedUrl}
            alt={translate(doc.label)}
            rootClassName="kyc-preview__image"
            // Full-screen viewer: zoom, wheel/drag, rotate, flip.
            preview={
              isUploading
                ? false
                : {
                    rootClassName: 'kyc-viewer',
                    mask: (
                      <span className="kyc-preview__zoom">
                        <ZoomInOutlined />{' '}
                        {translate('Click to zoom')}
                      </span>
                    ),
                  }
            }
          />
          {isSaved && (
            <figcaption className="kyc-preview__badge">
              <CheckCircleFilled /> {translate('Received')}
            </figcaption>
          )}
          {isUploading && (
            <div className="kyc-preview__overlay">
              <Spin />
              <span>{translate('Uploading securely...')}</span>
            </div>
          )}
        </figure>
      ) : (
        <Upload.Dragger
          accept={ACCEPTED_TYPES}
          showUploadList={false}
          multiple={false}
          className="kyc-drop"
          beforeUpload={(file) => {
            onPick(file as unknown as File);
            return false;
          }}
        >
          <span className="kyc-drop__icon">
            <CloudUploadOutlined />
          </span>
          <strong>{translate('Drag your photo here')}</strong>
          <span className="kyc-drop__or">
            {translate('or')} <u>{translate('browse your files')}</u>
          </span>
          <span className="kyc-drop__meta">
            PNG, JPG · {translate('up to')} {MAX_KYC_FILE_MB}MB
          </span>
        </Upload.Dragger>
      )}

      {status === 'error' && (
        <div className="kyc-step__error" role="alert">
          <ExclamationCircleFilled />
          <span>
            {errorMessage ||
              translate('Upload failed. Please try again.')}
          </span>
        </div>
      )}

      {!showCamera && (
        <div className="kyc-step__actions">
          {draftUrl ? (
            <>
              <Button
                size="large"
                onClick={onDiscard}
                disabled={isUploading}
              >
                {doc.liveCapture
                  ? translate('Retake')
                  : translate('Choose another')}
              </Button>
              <Button
                type="primary"
                size="large"
                className="kyc-btn-primary"
                loading={isUploading}
                onClick={onConfirm}
              >
                {status === 'error'
                  ? translate('Try again')
                  : translate('Use this photo')}
              </Button>
            </>
          ) : isSaved && doc.liveCapture ? (
            <Button
              size="large"
              icon={<CameraOutlined />}
              onClick={() => setRetaking(true)}
            >
              {translate('Retake selfie')}
            </Button>
          ) : isSaved ? (
            <Upload
              accept={ACCEPTED_TYPES}
              showUploadList={false}
              beforeUpload={(file) => {
                onPick(file as unknown as File);
                return false;
              }}
            >
              <Button size="large" icon={<ReloadOutlined />}>
                {translate('Replace')}
              </Button>
            </Upload>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default KycDocumentStep;
